import React, { useState, useMemo, useEffect } from "react";
import { FleetrifyBgs } from "../../../assets/video";
import { FLEETRIFYROUTEPATH } from "../../../routes/routesPath";
import { FleetrifyInputField } from "../../../components/Input/input";
import { FleetrifyButton } from "../../../components/Button/customButton";
import { fleetrifyIcons } from "../../../assets/icons/icons";
import { EyeOff, CheckCircle } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import CarouselCard from "../../../components/Carousel";
import { useCreateAccount } from "../../../controllers/useCreateAccount";
import SignupBuddies, { BuddyFocus } from "../../../components/SignupBuddies";

const CreateAccount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const savedStorageAccountTypeId = localStorage.getItem('createAccountTypeId');
  const initialAccountTypeId = location.state?.account_type_id || savedStorageAccountTypeId || undefined;
  const initialAccountTypeName = location.state?.account_type || localStorage.getItem('createAccountTypeName') || undefined;

  const savedCreateAccountForm = (() => {
    if (typeof window === 'undefined') return { fullName: '', email: '', password: '' };
    const saved = localStorage.getItem('createAccountForm');
    if (!saved) return { fullName: '', email: '', password: '' };
    try {
      const parsed = JSON.parse(saved);
      return {
        fullName: parsed.fullName || '',
        email: parsed.email || '',
        password: parsed.password || '',
      };
    } catch {
      return { fullName: '', email: '', password: '' };
    }
  })();

  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState<BuddyFocus>("none");

  // Tell the avatars which field is active. Moving focus within the same field
  // (e.g. input -> show-password button) doesn't count as leaving it.
  const trackFocus = (field: BuddyFocus) => ({
    onFocus: () => setFocusedField(field),
    onBlur: (e: React.FocusEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusedField("none");
    },
  });
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formError, setFormError] = useState("");
  const [savedAccountTypeId, setSavedAccountTypeId] = useState<string | undefined>(initialAccountTypeId);
  const [savedAccountTypeName, setSavedAccountTypeName] = useState<string | undefined>(initialAccountTypeName);

  const {
    fullName,
    setFullName,
    email,
    setEmail,
    password,
    setPassword,
    message,
    isLoading,
    handleSignup,
  } = useCreateAccount(savedAccountTypeId, savedCreateAccountForm, savedAccountTypeName);

  const passwordRequirements = useMemo(() => {
    return {
      minLength: password.length >= 8,
      upperAndLower: /(?=.*[a-z])(?=.*[A-Z])/.test(password),
      symbol: /[^A-Za-z0-9]/.test(password),
      number: /[0-9]/.test(password),
    };
  }, [password]);

  const allPasswordRequirementsMet =
  passwordRequirements.minLength &&
  passwordRequirements.upperAndLower &&
  passwordRequirements.symbol &&
  passwordRequirements.number;

  useEffect(() => {
    if (location.state?.termsAccepted) {
      setAgreedToTerms(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (initialAccountTypeId) {
      setSavedAccountTypeId(initialAccountTypeId);
      localStorage.setItem('createAccountTypeId', initialAccountTypeId);
    }
    if (initialAccountTypeName) {
      setSavedAccountTypeName(initialAccountTypeName);
      localStorage.setItem('createAccountTypeName', initialAccountTypeName);
    }
  }, [initialAccountTypeId, initialAccountTypeName]);

  // Save form state whenever it changes
  useEffect(() => {
    const formData = {
      fullName,
      email,
      password,
      agreedToTerms,
      account_type_id: savedAccountTypeId,
    };
    localStorage.setItem('createAccountForm', JSON.stringify(formData));
    if (savedAccountTypeId) {
      localStorage.setItem('createAccountTypeId', savedAccountTypeId);
    }
  }, [fullName, email, password, agreedToTerms, savedAccountTypeId]);

  const accountType = useMemo(() => {
    const name = savedAccountTypeName?.toLowerCase();
    if (name === "organization") return "company";
    if (name === "individual") return "individual";
    return null;
  }, [savedAccountTypeName]);

  const heading =
    accountType === "company"
      ? "Create an Organisation Account"
      : "Create an Individual Account";

  const description =
    accountType === "company"
      ? "Register your organisation to manage fleet operations and logistics."
      : "Sign up to manage your personal logistics and track your fleet efficiently.";

  const fullNamePlaceholder =
    accountType === "company" ? "Organisation Name" : "Full Name";

  const slides = [
    { title: "Welcome to Fleetrify", description: "Login to manage logistics and fleet operations" },
    { title: "Real-time Fleet Insights", description: "Get live updates and analytics on every vehicle in your fleet" },
    { title: "Simplify Logistics Management", description: "Automate routing, tracking, and reporting — all in one platform" },
  ];

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fullName.trim() || !email.trim() || !password.trim()) {
      setFormError("All fields are required");
      return;
    }

    if (!agreedToTerms) {
      setFormError("You must agree to the Terms & Conditions to continue.");
      return;
    }

    setFormError("");
    const result = await handleSignup(agreedToTerms);

    // Already verified: send them to log in.
    if (result.alreadyRegistered) {
      toast.info(result.message);
      navigate(FLEETRIFYROUTEPATH.LOGIN);
      return;
    }

    // Signed up before but never verified: a new code was emailed — verify, then log in.
    if (result.success && result.returning && result.email) {
      toast.success(result.message || "Welcome back! We sent a new code to your email.");
      localStorage.removeItem('createAccountForm');
      localStorage.removeItem('createAccountTypeId');
      localStorage.removeItem('createAccountTypeName');
      navigate(FLEETRIFYROUTEPATH.VERIFY_EMAIL, { state: { email: result.email, returning: true } });
      return;
    }

    if (result.success && result.email) {
      // Clear saved form data on successful signup
      localStorage.removeItem('createAccountForm');
      localStorage.removeItem('createAccountTypeId');
      localStorage.removeItem('createAccountTypeName');
      navigate(FLEETRIFYROUTEPATH.VERIFY_EMAIL, { state: { email: result.email } });
    }
  };

  return (
    <div className="relative flex flex-col lg:flex-row min-h-screen items-center justify-center lg:bg-transparent bg-gray-100">
      {/* video section */}
      <div className="relative w-full lg:w-[45%] h-screen lg:h-screen order-2 lg:order-none">
        <video className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline>
          <source src={FleetrifyBgs} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black opacity-50 z-10"></div>
        <div className="absolute z-20 inset-0 flex items-center justify-center">
          <CarouselCard slides={slides} />
        </div>
      </div>

      {/* form section */}
      {/* On mobile the form floats over the video; let it scroll when it's taller than the screen
          (items-start + my-auto keeps it centred when it fits, without cutting off the top) */}
      <div className="w-full lg:w-[55%] flex justify-center items-start lg:items-center absolute lg:static z-30 px-5 top-0 bottom-0 overflow-y-auto lg:overflow-visible py-6 lg:py-0">
        <form
          onSubmit={onSubmit}
          className="w-[430px] sm:w-[340px] md:w-[500px] lg:w-[430px] max-w-full bg-white px-4 py-6 rounded space-y-4 my-auto"
        >
          <SignupBuddies
            focus={focusedField}
            name={fullName}
            email={email}
            password={password}
            showPassword={showPassword}
            checks={passwordRequirements}
            agreedToTerms={agreedToTerms}
            isCompany={accountType === "company"}
          />

          <h2 className="text-2xl font-bold text-center text-[#143887]">{heading}</h2>
          <p className="text-center text-gray-600">{description}</p>

          <div {...trackFocus("name")}>
            {Array.isArray(message?.name) && (
              <label className="text-sm text-red-500">
                *{message.name[0]}
              </label>
            )}
            <FleetrifyInputField
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              type="text"
              icon={fleetrifyIcons.addNewUserIcon}
              placeholder={fullNamePlaceholder}
              required
            />
          </div>

          <div {...trackFocus("email")}>
            {Array.isArray(message?.email) && (
              <label className="text-sm text-red-500">
                *{message.email[0]}
              </label>
            )}
            <FleetrifyInputField
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              icon={fleetrifyIcons.mailIcon}
              placeholder="Email"
              required
            />
          </div>

          <div {...trackFocus("password")}>
            {Array.isArray(message?.password) && (
              <label className="text-sm text-red-500">
                *{message.password.length > 1
                  ? `${message.password[0]} (and ${message.password.length - 1} more...)`
                  : message.password[0]}
              </label>
            )}
            <FleetrifyInputField
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyUp={() => setShowPasswordRequirements(true)}
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              required
              icon={
                <button type="button" onClick={() => setShowPassword((prev) => !prev)}>
                  {showPassword ? <EyeOff size={16} color="#333" /> : fleetrifyIcons.viewIcon}
                </button>
              }
            />
            {/* {showPasswordRequirements && (
              <div className="mt-3 space-y-2">
                <label className="flex items-start gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={passwordRequirements.minLength}
                    readOnly
                    disabled
                    className={`mt-1 h-4 w-4 rounded border ${passwordRequirements.minLength ? 'border-green-500 bg-green-500' : 'border-gray-300 bg-white'}`}
                  />
                  <span className={passwordRequirements.minLength ? 'text-green-700' : 'text-gray-600'}>
                    Password must be at least 8 characters
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={passwordRequirements.upperAndLower}
                    readOnly
                    disabled
                    className={`mt-1 h-4 w-4 rounded border ${passwordRequirements.upperAndLower ? 'border-green-500 bg-green-500' : 'border-gray-300 bg-white'}`}
                  />
                  <span className={passwordRequirements.upperAndLower ? 'text-green-700' : 'text-gray-600'}>
                    Must contain at least one uppercase and one lowercase letter
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={passwordRequirements.symbol}
                    readOnly
                    disabled
                    className={`mt-1 h-4 w-4 rounded border ${passwordRequirements.symbol ? 'border-green-500 bg-green-500' : 'border-gray-300 bg-white'}`}
                  />
                  <span className={passwordRequirements.symbol ? 'text-green-700' : 'text-gray-600'}>
                    Must contain at least one symbol
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={passwordRequirements.number}
                    readOnly
                    disabled
                    className={`mt-1 h-4 w-4 rounded border ${passwordRequirements.number ? 'border-green-500 bg-green-500' : 'border-gray-300 bg-white'}`}
                  />
                  <span className={passwordRequirements.number ? 'text-green-700' : 'text-gray-600'}>
                    Must contain at least one number
                  </span>
                </label>
              </div>
            )} */}
            {showPasswordRequirements && password && (
              <>
                {allPasswordRequirementsMet ? (
                  <div className="mt-3 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-700">
                    <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
                    <span className="font-medium">
                      Your password meets all requirements 🎉
                    </span>
                  </div>
                ) : (
                  <div className="mt-3 space-y-2">
                    <label className="flex items-start gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={passwordRequirements.minLength}
                        readOnly
                        disabled
                        className={`mt-1 h-4 w-4 rounded border ${
                          passwordRequirements.minLength
                            ? "border-green-500 bg-green-500"
                            : "border-gray-300 bg-white"
                        }`}
                      />
                      <span
                        className={
                          passwordRequirements.minLength
                            ? "text-green-700"
                            : "text-gray-600"
                        }
                      >
                        Password must be at least 8 characters
                      </span>
                    </label>

                    <label className="flex items-start gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={passwordRequirements.upperAndLower}
                        readOnly
                        disabled
                        className={`mt-1 h-4 w-4 rounded border ${
                          passwordRequirements.upperAndLower
                            ? "border-green-500 bg-green-500"
                            : "border-gray-300 bg-white"
                        }`}
                      />
                      <span
                        className={
                          passwordRequirements.upperAndLower
                            ? "text-green-700"
                            : "text-gray-600"
                        }
                      >
                        Must contain at least one uppercase and one lowercase letter
                      </span>
                    </label>

                    <label className="flex items-start gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={passwordRequirements.symbol}
                        readOnly
                        disabled
                        className={`mt-1 h-4 w-4 rounded border ${
                          passwordRequirements.symbol
                            ? "border-green-500 bg-green-500"
                            : "border-gray-300 bg-white"
                        }`}
                      />
                      <span
                        className={
                          passwordRequirements.symbol
                            ? "text-green-700"
                            : "text-gray-600"
                        }
                      >
                        Must contain at least one symbol
                      </span>
                    </label>

                    <label className="flex items-start gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={passwordRequirements.number}
                        readOnly
                        disabled
                        className={`mt-1 h-4 w-4 rounded border ${
                          passwordRequirements.number
                            ? "border-green-500 bg-green-500"
                            : "border-gray-300 bg-white"
                        }`}
                      />
                      <span
                        className={
                          passwordRequirements.number
                            ? "text-green-700"
                            : "text-gray-600"
                        }
                      >
                        Must contain at least one number
                      </span>
                    </label>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Terms & Conditions checkbox */}
          <div className="flex items-start gap-3">
            <input
              id="terms"
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => {
                setAgreedToTerms(e.target.checked);
                if (e.target.checked) setFormError("");
              }}
              className="mt-1 h-4 w-4 rounded border-gray-300 accent-[#143887] cursor-pointer flex-shrink-0"
            />
            <label htmlFor="terms" className="text-sm text-gray-600 leading-snug cursor-pointer">
              I agree to Fleetrify's{" "}
              <button
                type="button"
                onClick={() => {
                  // Save current form state before navigating
                  const formData = {
                    fullName,
                    email,
                    password,
                    agreedToTerms,
                    account_type_id: savedAccountTypeId,
                  };
                  localStorage.setItem('createAccountForm', JSON.stringify(formData));
                  if (savedAccountTypeId) {
                    localStorage.setItem('createAccountTypeId', savedAccountTypeId);
                  }
                  navigate(FLEETRIFYROUTEPATH.TERMS_AND_CONDITIONS);
                }}
                className="text-[#143887] font-semibold underline hover:text-blue-800 transition-colors"
              >
                Terms & Conditions
              </button>
            </label>
          </div>

          {(formError || message) && (
            <p className="text-center text-sm text-red-500">
              {formError || ((typeof message === "string") && message)}
            </p>
          )}

          <FleetrifyButton
            type="submit"
            className="w-full mt-4"
            disabled={isLoading || !agreedToTerms}
          >
            {isLoading ? "Creating Account..." : "Create Account"}
          </FleetrifyButton>

          <p className="text-center mt-2">
            Already have an account?{" "}
            <Link to={FLEETRIFYROUTEPATH.LOGIN} className="text-[#2F80ED] font-bold">
              Sign In
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default CreateAccount;